import { Collapse, Table, Tag, List, Space, Tooltip, Empty, Spin } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ExportOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import "./VerificationContent.css";
const { Panel } = Collapse;

const VerificationContent = ({ verificationData, loading, error, onCopy }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getOrderDetailsTableData = () => {
    if (!verificationData?.orderDetails) return [];
    const { orderId, createdAt, company, commodityId, sampleVariant } =
      verificationData.orderDetails;
    return [
      {
        field: "Order ID",
        platform: orderId?.appData || "-",
        onChain: orderId?.onChainData || "-",
        isMatched: orderId?.isMatched,
      },
      {
        field: "Created At",
        platform: createdAt?.appData || "-",
        onChain: createdAt?.onChainData || "-",
        isMatched: createdAt?.isMatched,
      },
      {
        field: "Company",
        platform: company?.appData || "-",
        onChain: company?.onChainData || "-",
        isMatched: company?.isMatched,
      },
      {
        field: "Commodity",
        platform: commodityId?.appData || "-",
        onChain: commodityId?.onChainData || "-",
        isMatched: commodityId?.isMatched,
      },
      {
        field: "Sample Variant",
        platform: sampleVariant?.appData || "-",
        onChain: sampleVariant?.onChainData || "-",
        isMatched: sampleVariant?.isMatched,
      },
    ];
  };

  const getSampleDetailsTableData = () => {
    if (!verificationData?.sampleDetails) return [];
    const {
      quantityOfSample,
      dateOfPurchase,
      manufacturingDate,
      expiryDate,
    } = verificationData.sampleDetails;
    return [
      {
        field: "Quantity",
        platform: quantityOfSample?.appData || "-",
        onChain: quantityOfSample?.onChainData || "-",
        isMatched: quantityOfSample?.isMatched,
      },
      {
        field: "Purchase Date",
        platform: dateOfPurchase?.appData || "-",
        onChain: dateOfPurchase?.onChainData || "-",
        isMatched: dateOfPurchase?.isMatched,
      },
      {
        field: "Manufacturing Date",
        platform: manufacturingDate?.appData || "-",
        onChain: manufacturingDate?.onChainData || "-",
        isMatched: manufacturingDate?.isMatched,
      },
      {
        field: "Expiry Date",
        platform: expiryDate?.appData || "-",
        onChain: expiryDate?.onChainData || "-",
        isMatched: expiryDate?.isMatched,
      },
    ];
  };

  const getLabResponseTableData = () => {
    if (!verificationData?.orderCompletedDetails) return [];
    const {
      overallSafetyCompliant,
      overallQualityCompliant,
      overallSampleClassification,
    } = verificationData.orderCompletedDetails;
    return [
      {
        field: "Safety Compliant",
        platform: overallSafetyCompliant?.appData || "-",
        onChain: overallSafetyCompliant?.onChainData || "-",
        isMatched: overallSafetyCompliant?.isMatched,
      },
      {
        field: "Quality Compliant",
        platform: overallQualityCompliant?.appData || "-",
        onChain: overallQualityCompliant?.onChainData || "-",
        isMatched: overallQualityCompliant?.isMatched,
      },
      {
        field: "Sample Classification",
        platform: overallSampleClassification?.appData || "-",
        onChain: overallSampleClassification?.onChainData || "-",
        isMatched: overallSampleClassification?.isMatched,
      },
    ];
  };

  const columns = [
    {
      title: "Field",
      dataIndex: "field",
      key: "field",
      render: (text) => <b>{text}</b>,
      width: 100,
      ellipsis: true,
    },
    {
      title: "Platform Value",
      dataIndex: "platform",
      key: "platform",
      width: 180,
      ellipsis: true,
      render: text => <span style={{ wordBreak: "break-all" }}>{text}</span>,
    },
    {
      title: "On-Chain Value",
      dataIndex: "onChain",
      key: "onChain",
      width: 180,
      ellipsis: true,
      render: text => <span style={{ wordBreak: "break-all" }}>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "isMatched",
      key: "isMatched",
      width: 80,
      ellipsis: true,
      render: (isMatched) => {
        if (isMatched === null || isMatched === undefined) {
          return <span>-</span>;
        }

        return (
          <Tag
            style={{ padding: '3px 8px', borderRadius: '6px' }}
            color={isMatched ? "green" : "red"}
            icon={isMatched ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          >
            {isMatched ? "Matched" : "Mismatch"}
          </Tag>
        );
      },
    },
  ];

  const SummaryCard = ({ title, value, color }) => (
    <div
      style={{
        textAlign: "center",
      }}
    >
      <h2 className="verification-summary-value" style={{ color }}>{value}</h2>
      <p className="verification-summary-title" style={{ marginBottom: 0 }}>{title}</p>
    </div>
  );

  const renderTransactionHeader = (txnId) => {
    const link = `https://hashscan.io/testnet/transaction/${txnId}`;

    if (isMobile) {
      return (
        <div
          className="verification-txn-header"
          style={{
            background: "#E8F3F4",
            padding: "14px 16px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <b className="verification-header-title" style={{ display: "block", marginBottom: 8 }}>
              Transaction ID:
            </b>
            <div
              className="verification-txn-id"
              style={{
                background: "white",
                padding: "8px 12px",
                borderRadius: 6,
                fontWeight: 500,
                border: "1px solid #d9d9d9",
                wordBreak: "break-all",
                marginBottom: 12,
              }}
            >
              {txnId || "N/A"}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "8px 12px",
                  width: "100%",
                  borderRadius: 6,
                  border: "1px solid #d9d9d9",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                <span className="verification-icon"><ExportOutlined /></span>
                <span className="verification-button-text">View on Hedera</span>
              </button>
            </a>

            <Tooltip title="Copy Link">
              <button
                onClick={() => onCopy(txnId)}
                style={{
                  border: "1px solid #d9d9d9",
                  background: "white",
                  padding: "8px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                <span className="verification-icon"><ShareAltOutlined /></span>
              </button>
            </Tooltip>
          </div>
        </div>
      );
    }

    return (
      <div
        className="verification-txn-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#E8F3F4",
          padding: "14px 16px",
          borderRadius: 8,
          marginBottom: 12,
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <b className="verification-header-title">Transaction ID:</b>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            className="verification-txn-id"
            style={{
              background: "white",
              padding: "4px 12px",
              borderRadius: 6,
              fontWeight: 500,
              border: "1px solid #d9d9d9",
            }}
          >
            {txnId || "N/A"}
          </div>

          <a href={link} target="_blank" rel="noopener noreferrer">
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "0px 10px",
                height: 26,
                borderRadius: 6,
                border: "1px solid #d9d9d9",
                background: "white",
                cursor: "pointer",
              }}
            >
              <span className="verification-icon"><ExportOutlined /></span>
              <span className="verification-button-text">View on Hedera</span>
            </button>
          </a>

          <Tooltip title="Copy Link">
            <button
              onClick={() => onCopy(txnId)}
              style={{
                border: "1px solid #d9d9d9",
                background: "white",
                padding: "4px 8px",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <span className="verification-icon"><ShareAltOutlined /></span>
            </button>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderMobileFieldCard = (item) => {
    const getStatusColor = () => {
      if (item.isMatched === null || item.isMatched === undefined) return "#d9d9d9";
      return item.isMatched ? "#52c41a" : "#ff4d4f";
    };

    return (
      <div
        key={item.field}
        className="verification-mobile-card"
        style={{
          background: "white",
          borderRadius: 8,
          padding: "16px",
          marginBottom: 12,
          border: "1px solid #d9d9d9",
          borderLeft: `4px solid ${getStatusColor()}`,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <b className="verification-mobile-field-title" style={{ color: "#333" }}>
            Field: {item.field}
          </b>
        </div>
        
        <div style={{ marginBottom: 8 }}>
          <div className="verification-mobile-label" style={{ color: "#666", marginBottom: 4 }}>
            Platform Value:
          </div>
          <div className="verification-mobile-value" style={{ wordBreak: "break-all" }}>
            {item.platform}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div className="verification-mobile-label" style={{ color: "#666", marginBottom: 4 }}>
            On-Chain Value:
          </div>
          <div className="verification-mobile-value" style={{ wordBreak: "break-all" }}>
            {item.onChain}
          </div>
        </div>

        <div>
          <div className="verification-mobile-label" style={{ color: "#666", marginBottom: 4 }}>
            Status:
          </div>
          {item.isMatched === null || item.isMatched === undefined ? (
            <span>-</span>
          ) : (
            <Tag
              style={{ padding: '3px 8px', borderRadius: '6px' }}
              color={item.isMatched ? "green" : "red"}
              icon={item.isMatched ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            >
              {item.isMatched ? "Matched" : "Mismatch"}
            </Tag>
          )}
        </div>
      </div>
    );
  };

  const renderDocuments = (artefacts) => {
    return (
      <>
        <h4 className="verification-section-title">Supporting Documents</h4>

        {!artefacts || artefacts.length === 0 ? (
          <div style={{ padding: "8px 0", fontSize: 16 }}>-</div>
        ) : (
          <List
            dataSource={artefacts}
            renderItem={(doc) => (
              <List.Item
                actions={[
                  <Tooltip title="View Document" key="view">
                    <button
                      onClick={() => window.open(doc.url, "_blank")}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <span className="verification-icon"><EyeOutlined /></span>
                    </button>
                  </Tooltip>,
                  <Tooltip title="Download" key="download">
                    <a href={doc.url} download style={{ cursor: "pointer" }}>
                      <span className="verification-icon"><DownloadOutlined /></span>
                    </a>
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  title={
                    doc.type?.replace(/([A-Z])/g, " $1").trim() || "Document"
                  }
                />
              </List.Item>
            )}
          />
        )}
      </>
    );
  };

  const getSectionStatus = (data) => {
    if (!data) return { verified: false, color: "default", label: "No Data Found" };
    const fields = Object.values(data).filter(
      (val) => val && typeof val === "object" && "isMatched" in val
    );
    const allEmpty = fields.every(
      (field) => field.isMatched === null || field.isMatched === undefined
    );
    if (allEmpty) return { verified: false, color: "default", label: "No Data Found" };
    const allMatched = fields.every((field) => field.isMatched === true);
    return {
      verified: allMatched,
      color: allMatched ? "green" : "red",
      label: allMatched ? "Verified" : "Mismatch Found",
    };
  };

  return (
    <div className="verification-content">
      <Spin
        spinning={loading}
        tip="Loading verification data..."
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      >
        {error ? (
          <Empty description={error} />
        ) : verificationData ? (
          <div style={{ 
            width: "100%", 
            maxWidth: isMobile ? "100%" : "700px",
            margin: "0 auto"
          }}>
            <div
              className="verification-summary-container"
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 20,
                backgroundColor: "#d1e8ea",
                padding: isMobile ? "20px 16px" : "24px 60px",
                borderRadius: "12px",
                flexWrap: "wrap",
                gap: isMobile ? "16px" : "0",
              }}
            >
              <SummaryCard
                title="Total Fields"
                value={verificationData.meta?.totalFields || 0}
                color="#52c41a"
              />
              <SummaryCard
                title="Matched Fields"
                value={verificationData.meta?.matchedCount || 0}
                color="#52c41a"
              />
              <SummaryCard
                title="Mismatches"
                value={verificationData.meta?.unmatchedCount || 0}
                color="#ff4d4f"
              />
            </div>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Collapse defaultActiveKey={['1']}>
                {verificationData.orderDetails && (
                  <Panel
                    key="1"
                    header={
                      <div
                        style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}
                      >
                        <b>Order Details</b>
                        <Tag
                          style={{ padding: '3px 8px', borderRadius: '6px' }}
                          color={
                            getSectionStatus(verificationData.orderDetails).color
                          }
                          icon={
                            getSectionStatus(verificationData.orderDetails)
                              .verified ? (
                              <CheckCircleOutlined />
                            ) : (
                              <CloseCircleOutlined />
                            )
                          }
                        >
                          {getSectionStatus(verificationData.orderDetails).label}
                        </Tag>
                      </div>
                    }
                  >
                    {renderTransactionHeader(verificationData.orderDetails.txnId)}
                    {isMobile ? (
                      <div>
                        {getOrderDetailsTableData().map(renderMobileFieldCard)}
                      </div>
                    ) : (
                      <Table
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={getOrderDetailsTableData()}
                        rowKey="field"
                      />
                    )}
                  </Panel>
                )}
              </Collapse>

              <Collapse>
                {verificationData.sampleDetails && (
                  <Panel
                    header={
                      <div
                        style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}
                      >
                        <b>Sample Details</b>
                        <Tag
                          style={{ padding: '3px 8px', borderRadius: '6px' }}
                          color={
                            getSectionStatus(verificationData.sampleDetails).color
                          }
                          icon={
                            getSectionStatus(verificationData.sampleDetails)
                              .verified ? (
                              <CheckCircleOutlined />
                            ) : (
                              <CloseCircleOutlined />
                            )
                          }
                        >
                          {getSectionStatus(verificationData.sampleDetails).label}
                        </Tag>
                      </div>
                    }
                    key="2"
                  >
                    {renderTransactionHeader(verificationData.sampleDetails.txnId)}
                    {isMobile ? (
                      <div>
                        {getSampleDetailsTableData().map(renderMobileFieldCard)}
                      </div>
                    ) : (
                      <Table
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={getSampleDetailsTableData()}
                        rowKey="field"
                      />
                    )}
                    {renderDocuments(verificationData.sampleDetails.artefact)}
                  </Panel>
                )}
              </Collapse>
              
              <Collapse>
                {verificationData.orderCompletedDetails && (
                  <Panel
                    header={
                      <div
                        style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}
                      >
                        <b>Lab Response</b>
                        <Tag
                          style={{ padding: '3px 8px', borderRadius: '6px' }}
                          color={
                            getSectionStatus(verificationData.orderCompletedDetails)
                              .color
                          }
                          icon={
                            getSectionStatus(verificationData.orderCompletedDetails)
                              .verified ? (
                              <CheckCircleOutlined />
                            ) : (
                              <CloseCircleOutlined />
                            )
                          }
                        >
                          {
                            getSectionStatus(verificationData.orderCompletedDetails)
                              .label
                          }
                        </Tag>
                      </div>
                    }
                    key="3"
                  >
                    {renderTransactionHeader(
                      verificationData.orderCompletedDetails.txnId
                    )}
                    {isMobile ? (
                      <div>
                        {getLabResponseTableData().map(renderMobileFieldCard)}
                      </div>
                    ) : (
                      <Table
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={getLabResponseTableData()}
                        rowKey="field"
                      />
                    )}
                    {renderDocuments(
                      verificationData.orderCompletedDetails.artefact
                    )}
                  </Panel>
                )}
              </Collapse>
            </Space>
          </div>
        ) : (
          <Empty description="No verification data available" />
        )}
      </Spin>
    </div>
  );
};

export default VerificationContent;